import React, { useState, useEffect } from 'react';
import { Box, Button, Grid } from '@mui/material';
import TabPanel from '@mui/lab/TabPanel';
import { PetPhoto } from '../../../types/PetPhoto';
import PetCard from './PetCard';
import EmptyPhotosMessage from './EmptyPhotosMessage';
import LoadingCards from './LoadingCards';
import { validateImageUrl } from '../../../utilities';

interface TabPanelProps {
  value: string;
  photos: PetPhoto[];
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  openMediaDialog: (img: string, date: string, name?: string) => void;
  setDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setUserId: React.Dispatch<React.SetStateAction<number | null>>;
}

function RenderTabPanel({
  value,
  photos,
  loading,
  hasMore,
  onLoadMore,
  openMediaDialog,
  setDrawerOpen,
  setUserId,
}: TabPanelProps) {
  const [validPhotos, setValidPhotos] = useState<PetPhoto[]>([]);
  const [validatedPhotos, setValidatedPhotos] = useState(false);

  useEffect(() => {
    setValidatedPhotos(false);
    const validatePhotos = async () => {
      const validPhotosArray: PetPhoto[] = [];

      const validationPromises = photos.map(async (photo) => {
        const isValid = await validateImageUrl(photo.url);
        return { photo, isValid };
      });

      const results = await Promise.all(validationPromises);

      results.forEach(({ photo, isValid }) => {
        if (isValid) {
          validPhotosArray.push(photo);
        }
      });

      setValidPhotos(validPhotosArray);
      setValidatedPhotos(true);
    };

    if (photos.length > 0) {
      validatePhotos();
    } else {
      setValidPhotos([]);
      setValidatedPhotos(true);
    }
  }, [photos]);

  return (
    <TabPanel value={value} sx={{ px: 3, pt: 6 }}>
      {(loading || !validatedPhotos) && <LoadingCards />}
      {(!loading && validatedPhotos && (!validPhotos || validPhotos.length === 0)) && (
        <EmptyPhotosMessage />
      )}
      {!loading && validPhotos && validPhotos.length > 0 && (
        <Box>
          <Grid container spacing={3}>
            {validPhotos.map((photo) => (
              <PetCard
                key={photo.id}
                photo={photo}
                careType={value}
                setDrawerOpen={setDrawerOpen}
                setUserId={setUserId}
                openMediaDialog={openMediaDialog}
              />
            ))}
          </Grid>
          <Box sx={{
            display: 'flex', justifyContent: 'center', mt: 4, mb: 2,
          }}
          >
            {hasMore && (
              <Button
                type="button"
                variant="text"
                onClick={onLoadMore}
                disabled={loading}
              >
                {loading ? 'Loading' : 'Show More'}
              </Button>
            )}
          </Box>
        </Box>
      )}
    </TabPanel>
  );
}

export default RenderTabPanel;
